import React from 'react';
import { Play, ExternalLink } from 'lucide-react';

const videos = [
  {
    id: 1,
    title: 'Understanding the 5 Stages of Grief',
    description: 'A comprehensive guide to the Kübler-Ross grief model',
    thumbnail: 'https://images.unsplash.com/photo-1534330207526-8e81f10ec6fc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxncmllZnxlbnwwfHx8fDE3MzM5MjQxNzN8MA&ixlib=rb-4.1.0&q=85',
    videoId: 'gsYL4PC0hyk', // Example YouTube video ID
    duration: '12:45'
  },
  {
    id: 2,
    title: 'Coping with Grief and Loss',
    description: 'Practical strategies for navigating your grief journey',
    thumbnail: 'https://images.unsplash.com/photo-1527080392769-79f1e8fdb5a4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwyfHxncmllZnxlbnwwfHx8fDE3MzM5MjQxNzN8MA&ixlib=rb-4.1.0&q=85',
    videoId: 'FlaMxnal65E', // Example
    duration: '15:22'
  },
  {
    id: 3,
    title: 'Supporting Someone Who is Grieving',
    description: 'How to help a loved one through their grief',
    thumbnail: 'https://images.unsplash.com/photo-1573497620053-ea5300f94f21?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwzfHxncmllZnxlbnwwfHx8fDE3MzM5MjQxNzN8MA&ixlib=rb-4.1.0&q=85',
    videoId: 'l2zLCCRT-nE', // Example
    duration: '10:18'
  },
  {
    id: 4,
    title: 'Healing from Loss',
    description: 'Finding hope and healing after experiencing loss',
    thumbnail: 'https://images.unsplash.com/photo-1470115636492-6d2b56f9146d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxoZWFsaW5nfGVufDB8fHx8MTczMzkyNDE3M3ww&ixlib=rb-4.1.0&q=85',
    duration: '18:30'
  }
];

const GriefVideos = () => {
  return (
    <section className="py-20 bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-600 rounded-full mb-4">
            <Play className="w-8 h-8 text-white ml-1" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Helpful Grief Support Videos
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Educational videos to help you understand and navigate your grief journey
          </p>
        </div>

        {/* Videos Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {videos.map((video) => (
            <div 
              key={video.id}
              className="bg-gray-800 rounded-xl overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group cursor-pointer"
              onClick={() => video.videoId && window.open(`https://www.youtube.com/watch?v=${video.videoId}`, '_blank')}
            >
              {/* Thumbnail */}
              <div className="relative h-64 overflow-hidden">
                <img 
                  src={video.thumbnail}
                  alt={video.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors duration-300"></div>
                
                {/* Play Button Overlay */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-2xl">
                    <Play className="w-10 h-10 text-white ml-1" />
                  </div>
                </div>

                {/* Duration */}
                <div className="absolute bottom-4 right-4 bg-black/80 px-3 py-1 rounded text-sm font-semibold">
                  {video.duration}
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-xl font-bold mb-2 group-hover:text-blue-400 transition-colors">
                  {video.title}
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  {video.description}
                </p>
                
                {video.videoId && (
                  <div className="mt-4 flex items-center gap-2 text-blue-400 text-sm font-semibold">
                    <span>Watch on YouTube</span>
                    <ExternalLink className="w-4 h-4" />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Additional Resources */}
        <div className="mt-16 bg-gray-800 rounded-2xl p-8 text-center">
          <h3 className="text-2xl font-bold mb-4">Looking for More Resources?</h3>
          <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
            Explore curated playlists and channels dedicated to grief support, mental health, 
            and healing from loss.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a 
              href="https://www.youtube.com/results?search_query=grief+support"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 font-semibold flex items-center gap-2"
            >
              <Play className="w-5 h-5" />
              Browse Grief Videos
            </a>
            <a 
              href="https://www.ted.com/topics/grief"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors duration-200 font-semibold flex items-center gap-2"
            >
              <ExternalLink className="w-5 h-5" />
              TED Talks on Grief
            </a>
          </div>
        </div>

        {/* Note */}
        <div className="mt-8 text-center">
          <p className="text-gray-400 text-sm">
            Note: These videos are for educational purposes. They don't replace professional grief counseling. 
            If you need personalized support, please reach out to a qualified therapist.
          </p>
        </div>
      </div>
    </section>
  );
};

export default GriefVideos;
